/**
 * Consultant Form Page (Create/Edit)
 */
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Paper,
  InputAdornment,
  ListSubheader,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { PageHeader, ImageSizeHint } from '../../components/common';
import { IMAGE_SIZES } from '../../constants/imageSizes';
import {
  useGetConsultantQuery,
  useCreateConsultantMutation,
  useUpdateConsultantMutation,
} from '../../store/api/consultantsApi';
import { useGetSubServicesQuery } from '../../store/api/servicesApi';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import ProviderDocumentsPendingSection from '../../components/providers/ProviderDocumentsPendingSection';
import ProviderDocumentsSection from '../../components/providers/ProviderDocumentsSection';
import { useCreateProviderDocumentMutation } from '../../store/api/providerDocumentsApi';
import type { PendingProviderDocument } from '../../types/providerDocument';
import { uploadPendingProviderDocuments } from '../../utils/uploadProviderDocuments';
import { usePermissions } from '../../hooks/usePermissions';
import ProviderProfileFields from '../../components/providers/ProviderProfileFields';
import type { ProviderProfile } from '../../types/providerProfile';
import {
  appendProviderProfile,
  emptyProviderProfile,
  firstApiFieldError,
  providerProfileFromApi,
  validateProviderProfile,
} from '../../utils/providerProfile';

const ConsultantFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isEdit = !!id;

  const { data: consultantData, isLoading: isLoadingConsultant } = useGetConsultantQuery(Number(id!), {
    skip: !isEdit,
  });

  // Fetch all subservices - use a large page_size to get all items
  const { data: subservicesData, isLoading: isLoadingSubservices } = useGetSubServicesQuery({
    page: 1,
    page_size: 1000, // Large page size to get all items
  });

  const { hasPermission } = usePermissions();
  const canManageConsultants = hasPermission('manage_consultants');

  const [createConsultant, { isLoading: isCreating }] = useCreateConsultantMutation();
  const [updateConsultant, { isLoading: isUpdating }] = useUpdateConsultantMutation();
  const [createProviderDocument] = useCreateProviderDocumentMutation();

  const [pendingDocuments, setPendingDocuments] = useState<PendingProviderDocument[]>([]);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    age: '' as string | number,
    specialization: '' as string | number,
  });
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [subserviceSearch, setSubserviceSearch] = useState('');
  // Only send the specialization on edit when it was changed here, so saving never clears it by accident
  const [specializationTouched, setSpecializationTouched] = useState(false);
  const [profile, setProfile] = useState<ProviderProfile>(emptyProviderProfile);

  // Load consultant data when editing
  useEffect(() => {
    if (isEdit && consultantData?.data && subservicesData?.results) {
      const consultant = consultantData.data;
      // Find subservice ID by name
      const subservice = subservicesData.results.find((s) => s.name === consultant.specialization);
      setFormData({
        full_name: consultant.full_name,
        email: consultant.email || '',
        phone_number: consultant.phone_number,
        age: consultant.age || '',
        specialization: subservice?.id || '',
      });
      setProfilePreview(consultant.profile_picture || null);
      setProfile(providerProfileFromApi(consultant));
    }
  }, [isEdit, consultantData, subservicesData]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = t('consultants.form.errors.fullNameRequired');
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = t('consultants.form.errors.emailInvalid');
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = t('consultants.form.errors.phoneRequired');
    } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone_number.replace(/\s/g, ''))) {
      newErrors.phone_number = t('consultants.form.errors.phoneInvalid');
    }

    if (formData.age && (isNaN(Number(formData.age)) || Number(formData.age) < 18 || Number(formData.age) > 100)) {
      newErrors.age = t('consultants.form.errors.ageRange');
    }

    Object.entries(validateProviderProfile(profile)).forEach(([field, key]) => {
      newErrors[field] = t(key);
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('consultants.toast.fixErrors'));
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('full_name', formData.full_name);
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('phone_number', formData.phone_number);
      if (formData.age) {
        formDataToSend.append('age', formData.age.toString());
      }
      if (!isEdit || specializationTouched || formData.specialization) {
        formDataToSend.append('specialization', formData.specialization.toString());
      }
      appendProviderProfile(formDataToSend, profile);
      if (profileFile) {
        formDataToSend.append('profile_picture', profileFile);
      }

      if (isEdit) {
        await updateConsultant({ id: Number(id!), data: formDataToSend }).unwrap();
        toast.success(t('consultants.toast.updateSuccess'));
        navigate(`/consultants/${id}`);
      } else {
        const result = await createConsultant(formDataToSend).unwrap();
        if (pendingDocuments.length > 0) {
          await uploadPendingProviderDocuments(
            'consultants',
            result.data.id,
            pendingDocuments,
            createProviderDocument,
          );
        }
        toast.success(t('consultants.toast.createSuccess'));
        navigate('/consultants');
      }
    } catch (err: any) {
      toast.error(
        firstApiFieldError(err) ||
          err?.data?.message ||
          (isEdit ? t('consultants.toast.updateError') : t('consultants.toast.createError')),
      );
    }
  };

  if (isEdit && isLoadingConsultant) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={isEdit ? t('consultants.editTitle') : t('consultants.createTitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.consultants'), path: '/consultants' },
          { label: isEdit ? t('common.edit') : t('common.create') },
        ]}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/consultants')}
        sx={{ mb: 3 }}
      >
        {t('consultants.buttons.backToList')}
      </Button>

      <Card>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Profile Picture Section */}
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  {t('consultants.form.sections.profilePicture')}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={profilePreview || undefined}
                      alt="Profile"
                      sx={{ width: 120, height: 120, border: '2px solid', borderColor: 'divider' }}
                    >
                      <PersonIcon sx={{ fontSize: 60 }} />
                    </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="profile-picture-upload"
                        type="file"
                        onChange={handleProfileChange}
                      />
                      <label htmlFor="profile-picture-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          size="medium"
                        >
                          {profilePreview ? t('consultants.buttons.changePicture') : t('consultants.buttons.uploadPicture')}
                        </Button>
                      </label>
                      {profilePreview && (
                        <Button
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => {
                            setProfileFile(null);
                            setProfilePreview(isEdit && consultantData?.data?.profile_picture ? consultantData.data.profile_picture : null);
                          }}
                        >
                          {t('consultants.buttons.removePicture')}
                        </Button>
                      )}
                      <ImageSizeHint size={IMAGE_SIZES.profile} />
                    </Box>
                  </Box>
                </Paper>
              </Box>

              <Divider />

              {/* Personal Information Section */}
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  {t('consultants.form.sections.personalInfo')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label={t('consultants.form.fullName')}
                      value={formData.full_name}
                      onChange={(e) => {
                        handleInputChange('full_name', e.target.value);
                        if (errors.full_name) {
                          setErrors((prev) => ({ ...prev, full_name: '' }));
                        }
                      }}
                      error={!!errors.full_name}
                      helperText={errors.full_name}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="email"
                      label={t('consultants.form.email')}
                      value={formData.email}
                      onChange={(e) => {
                        handleInputChange('email', e.target.value);
                        if (errors.email) {
                          setErrors((prev) => ({ ...prev, email: '' }));
                        }
                      }}
                      error={!!errors.email}
                      helperText={errors.email || t('providers.form.emailOptional')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label={t('consultants.form.phoneNumber')}
                      value={formData.phone_number}
                      onChange={(e) => {
                        handleInputChange('phone_number', e.target.value);
                        if (errors.phone_number) {
                          setErrors((prev) => ({ ...prev, phone_number: '' }));
                        }
                      }}
                      error={!!errors.phone_number}
                      helperText={errors.phone_number || t('consultants.form.phoneHelper')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('consultants.form.age')}
                      value={formData.age}
                      onChange={(e) => {
                        const value = e.target.value === '' ? '' : Number(e.target.value);
                        handleInputChange('age', value);
                        if (errors.age) {
                          setErrors((prev) => ({ ...prev, age: '' }));
                        }
                      }}
                      error={!!errors.age}
                      helperText={errors.age || t('consultants.form.ageHelper')}
                      inputProps={{
                        min: 18,
                        max: 100,
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Professional Information Section */}
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WorkIcon color="primary" />
                  {t('consultants.form.sections.professionalInfo')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth error={!!errors.specialization}>
                      <InputLabel id="specialization-label" shrink>
                        {t('consultants.form.specialization')}
                      </InputLabel>
                      <Select
                        labelId="specialization-label"
                        value={formData.specialization}
                        label={t('consultants.form.specialization')}
                        onChange={(e) => {
                          handleInputChange('specialization', e.target.value);
                          setSpecializationTouched(true);
                          if (errors.specialization) {
                            setErrors((prev) => ({ ...prev, specialization: '' }));
                          }
                        }}
                        displayEmpty
                        renderValue={(selected: string | number) => {
                          if (!selected || selected === '') {
                            return (
                              <span style={{ color: theme.palette.text.disabled }}>
                                {t('consultants.form.selectSpecialization')}
                              </span>
                            );
                          }
                          const selectedSubservice = subservicesData?.results?.find((s) => s.id === Number(selected));
                          return selectedSubservice ? selectedSubservice.name : '';
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 400,
                              // Keep the list readable instead of stretching across the screen
                              width: 'min(520px, calc(100vw - 32px))',
                            },
                          },
                          autoFocus: false,
                        }}
                        sx={{
                          '& .MuiSelect-select': {
                            paddingTop: '16.5px',
                            paddingBottom: '16.5px',
                          },
                        }}
                      >
                        {/* Search Field */}
                        <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder={t('consultants.form.searchSubservices')}
                            value={subserviceSearch}
                            onChange={(e) => setSubserviceSearch(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <WorkIcon fontSize="small" color="action" />
                                </InputAdornment>
                              ),
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </Box>
                        <MenuItem value="">
                          <em>{t('providers.form.noSpecialization')}</em>
                        </MenuItem>
                        {isLoadingSubservices ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            {t('consultants.form.loadingSubservices')}
                          </MenuItem>
                        ) : (
                          (() => {
                            // Group subservices by service
                            const grouped = (subservicesData?.results || []).reduce((acc: any, subservice: any) => {
                              const serviceName = subservice.service_name || t('consultants.form.otherService');
                              if (!acc[serviceName]) {
                                acc[serviceName] = [];
                              }
                              acc[serviceName].push(subservice);
                              return acc;
                            }, {});

                            // Filter by search
                            const filteredGroups: any = {};
                            Object.keys(grouped).forEach((serviceName) => {
                              const filtered = grouped[serviceName].filter((subservice: any) =>
                                subservice.name.toLowerCase().includes(subserviceSearch.toLowerCase()) ||
                                subservice.description?.toLowerCase().includes(subserviceSearch.toLowerCase())
                              );
                              if (filtered.length > 0) {
                                filteredGroups[serviceName] = filtered;
                              }
                            });

                            // Render grouped items
                            const items: React.ReactNode[] = [];
                            Object.keys(filteredGroups).sort().forEach((serviceName) => {
                              items.push(
                                <ListSubheader key={serviceName} sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>
                                  {serviceName}
                                </ListSubheader>
                              );
                              filteredGroups[serviceName].forEach((subservice: any) => {
                                items.push(
                                  <MenuItem
                                    key={subservice.id}
                                    value={subservice.id}
                                    sx={{ pl: 4, whiteSpace: 'normal' }}
                                  >
                                    <Box>
                                      <Typography variant="body1">{subservice.name}</Typography>
                                      {subservice.description && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                          }}
                                        >
                                          {subservice.description}
                                        </Typography>
                                      )}
                                    </Box>
                                  </MenuItem>
                                );
                              });
                            });

                            if (items.length === 0 && subserviceSearch) {
                              return (
                                <MenuItem disabled>
                                  <Typography variant="body2" color="text.secondary">
                                    {t('consultants.form.noSubservicesMatch', { search: subserviceSearch })}
                                  </Typography>
                                </MenuItem>
                              );
                            }

                            return items.length > 0 ? items : (
                              <MenuItem disabled>
                                <Typography variant="body2" color="text.secondary">
                                  {t('consultants.form.noSubservicesAvailable')}
                                </Typography>
                              </MenuItem>
                            );
                          })()
                        )}
                      </Select>
                      {errors.specialization && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {errors.specialization}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                        {t('providers.form.specializationOptional')}
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <ProviderProfileFields value={profile} onChange={setProfile} errors={errors} />

              <Divider />

              {!isEdit ? (
                <ProviderDocumentsPendingSection
                  documents={pendingDocuments}
                  onChange={setPendingDocuments}
                />
              ) : (
                <ProviderDocumentsSection
                  kind="consultants"
                  providerId={Number(id!)}
                  canManage={canManageConsultants}
                  embedded
                />
              )}

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/consultants')}
                  disabled={isCreating || isUpdating}
                  size="large"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isCreating || isUpdating}
                  size="large"
                >
                  {isCreating || isUpdating ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {t('consultants.buttons.saving')}
                    </>
                  ) : isEdit ? (
                    t('consultants.buttons.updateConsultant')
                  ) : (
                    t('consultants.buttons.createConsultant')
                  )}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConsultantFormPage;

